import React, { useState, useEffect } from "react";
import { Switch, Button, Space, Typography, Alert, Row, Col } from "antd";
import { BellOutlined, MailOutlined, MobileOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import {
	fetchNotificationPreferences,
	updateNotificationPreferences,
	resetNotificationPreferences,
} from "../../../api/communications/notificationPreferencesApi";
import { NotificationPreferences } from "../../../types/communications/NotificationTypes";
import { SectionCard, InlineLoading } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

interface NotificationPreferencesTabProps {
	userId: UserUuid;
	companyId?: number;
}

const NotificationPreferencesTab: React.FC<NotificationPreferencesTabProps> = ({ userId, companyId }) => {
	const { showSuccess, showError } = useNotifications();
	const [preferences, setPreferences] = useState<NotificationPreferences>({
		user_id: userId,
		push_enabled: true,
		email_enabled: true,
		sms_enabled: false,
	});
	const [isDefault, setIsDefault] = useState<boolean>(true);
	const [loading, setLoading] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [hasChanges, setHasChanges] = useState<boolean>(false);

	useEffect(() => {
		loadPreferences();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId]);

	const loadPreferences = async () => {
		setLoading(true);
		try {
			const response = await fetchNotificationPreferences(userId);
			setPreferences(response.data);
			setIsDefault(response.is_default);
			setHasChanges(false);
		} catch (error) {
			console.error("Error loading notification preferences:", error);
			showError("Failed to load notification preferences");
		} finally {
			setLoading(false);
		}
	};

	const handleToggle = (key: keyof NotificationPreferences, value: boolean) => {
		setPreferences((prev) => ({ ...prev, [key]: value }));
		setHasChanges(true);
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const response = await updateNotificationPreferences({
				user_id: userId,
				company_id: companyId,
				push_enabled: preferences.push_enabled,
				email_enabled: preferences.email_enabled,
				sms_enabled: preferences.sms_enabled,
			});
			setPreferences(response.data);
			setIsDefault(false);
			setHasChanges(false);
			showSuccess("Notification preferences saved");
		} catch (error) {
			console.error("Error saving notification preferences:", error);
			showError("Failed to save notification preferences");
		} finally {
			setSaving(false);
		}
	};

	const handleReset = async () => {
		setSaving(true);
		try {
			const response = await resetNotificationPreferences(userId);
			setPreferences(response.data);
			setIsDefault(true);
			setHasChanges(false);
			showSuccess("Notification preferences reset to defaults");
		} catch (error) {
			console.error("Error resetting notification preferences:", error);
			showError("Failed to reset notification preferences");
		} finally {
			setSaving(false);
		}
	};

	const getEnabledChannels = (): string[] => {
		const channels: string[] = [];
		if (preferences.push_enabled) channels.push("Push");
		if (preferences.email_enabled) channels.push("Email");
		if (preferences.sms_enabled) channels.push("SMS");
		return channels;
	};

	if (loading) {
		return <InlineLoading />;
	}

	return (
		<div>
			{isDefault && (
				<Alert
					message="Using Default Preferences"
					description="You are currently using the system default notification preferences. Customise them below and save to set your own."
					type="info"
					showIcon
					style={{ marginBottom: 24 }}
				/>
			)}

			<Row gutter={[24, 16]}>
				<Col xs={24} md={8}>
					<SectionCard style={{ height: "100%" }}>
						<Space direction="vertical" style={{ width: "100%" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Space>
									<BellOutlined style={{ fontSize: 20, color: "var(--mg-primary)" }} />
									<Text strong>Push Notifications</Text>
								</Space>
								<Switch
									data-testid="notifications-prefs-push"
									checked={preferences.push_enabled}
									onChange={(checked) => handleToggle("push_enabled", checked)}
									disabled={saving}
								/>
							</div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Receive in-app notifications in real time via the notification bell.
							</Text>
						</Space>
					</SectionCard>
				</Col>

				<Col xs={24} md={8}>
					<SectionCard style={{ height: "100%" }}>
						<Space direction="vertical" style={{ width: "100%" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Space>
									<MailOutlined style={{ fontSize: 20, color: "var(--mg-info)" }} />
									<Text strong>Email Notifications</Text>
								</Space>
								<Switch
									data-testid="notifications-prefs-email"
									checked={preferences.email_enabled}
									onChange={(checked) => handleToggle("email_enabled", checked)}
									disabled={saving}
								/>
							</div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Receive notifications via email to your registered email address.
							</Text>
						</Space>
					</SectionCard>
				</Col>

				<Col xs={24} md={8}>
					<SectionCard style={{ height: "100%" }}>
						<Space direction="vertical" style={{ width: "100%" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Space>
									<MobileOutlined style={{ fontSize: 20, color: "var(--mg-success)" }} />
									<Text strong>SMS Notifications</Text>
								</Space>
								<Switch
									checked={preferences.sms_enabled}
									onChange={(checked) => handleToggle("sms_enabled", checked)}
									disabled={saving}
								/>
							</div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Receive notifications via text message to your registered phone number.
							</Text>
						</Space>
					</SectionCard>
				</Col>
			</Row>

			{/* Summary */}
			<SectionCard style={{ marginTop: 24 }}>
				<Text style={{ fontSize: 13 }}>
					<Text strong>Active channels: </Text>
					{getEnabledChannels().length > 0
						? getEnabledChannels().join(", ")
						: "None — you will not receive any notifications"}
				</Text>
			</SectionCard>

			{/* Actions */}
			<div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 8 }}>
				{!isDefault && (
					<Button
						icon={<ReloadOutlined />}
						onClick={handleReset}
						loading={saving}
					>
						Reset to Defaults
					</Button>
				)}
				<Button
					data-testid="notifications-prefs-save-btn"
					type="primary"
					icon={<SaveOutlined />}
					onClick={handleSave}
					loading={saving}
					disabled={!hasChanges}
				>
					Save Preferences
				</Button>
			</div>
		</div>
	);
};

export default NotificationPreferencesTab;
